<template>
  <BaseDialog
    :title="displayText.title"
    :btn1="displayText.btn.generate"
    :btn2="displayText.btn.cancel"
    :close-on-click-modal="false"
    :btn1Loading="loading"
    width="610px"
    @close="handleClose"
    @cancel="handleClose"
    @confirm="onConfirm"
  >
    <OverlayArea :active="loading">
      <FlexLayout col :gap="24" style="margin-bottom: 40px;">
        <!-- 說明文字 -->
        <div class="text-sm text-gray-600">
          {{ displayText.description }}
        </div>

        <!-- 表單 -->
        <BaseElForm
          ref="formRef"
          :model="formData"
          :rules="formRules"
          label-position="top"
        >
          <!-- 選擇圖片風格 -->
          <BaseElFormItem
            :label="displayText.formItemLabel.imageStyle"
            prop="style"
          >
            <BaseElSelect
              v-model="formData.imageStyle"
              :placeholder="displayText.placeholder.imageStyle"
              style="width: 100%"
              clearable
            >
              <BaseElSelectOption
                v-for="style in imageStyleOptions"
                :key="style.value"
                :label="style.label"
                :value="style.value"
              />
            </BaseElSelect>
          </BaseElFormItem>

          <BaseElFormItem
            :label="displayText.formItemLabel.otherStyle"
            prop="style"
          >
            <BaseElInput
              v-model="formData.textPrompt"
              :placeholder="displayText.placeholder.otherStyle"
              maxlength="20"
              show-word-limit
            />
            <div class="text-xs text-gray-500 mt-2"> {{ displayText.styleOrOtherStyle }} </div>
          </BaseElFormItem>

          <!-- 圖片放上特定 logo、文字 -->
          <FormItemTooltip
            :label="displayText.formItemLabel.enableCustomContent"
            prop="enableCustomContent"
            :tooltip="displayText.tooltip.customContent"
          >
            <FlexLayout align="center" :gap="8">
              <BaseElSwitch
                v-model="formData.enableCustomContent"
                :active-text="displayText.switch.yes"
                :inactive-text="displayText.switch.no"
              />
            </FlexLayout>
          </FormItemTooltip>

          <!-- 當啟用自訂內容時顯示 logo 上傳和等級文字設定 -->
          <template v-if="formData.enableCustomContent">
            <FlexLayout col :gap="16">
              <!-- Logo 上傳區域 (共用) -->
              <BaseElFormItem :label="displayText.formItemLabel.logo">
                <UploadFileWithFileNameButton
                  :upload-fn="uploadToS3"
                  @change="onUploadLogoChanged"
                />
              </BaseElFormItem>

              <!-- 等級文字設定 -->
              <div
                v-for="level in 4"
                :key="level"
              >
                <div class="level-header">
                  <span class="level-title">{{ $t('member.memberCardSetting.ai.level') }} {{ level }}</span>
                </div>
                <ElInputWrapper>
                  <BaseElInput
                    v-model="formData.levelSettings[level - 1].customText"
                    :placeholder="displayText.placeholder.customText"
                  />
                </ElInputWrapper>
              </div>
            </FlexLayout>
          </template>
        </BaseElForm>

        <!-- 警告提示 -->
        <FlexLayout align="center" :gap="8" class="text-warning">
          <SVGIcon icon="common-warning" fill="var(--warning)" />
          {{ displayText.warning }}
        </FlexLayout>
      </FlexLayout>

      <template #overlay>
        <FlexLayout col align="center" justify="center" :gap="16" class="w-full">
          <Spin :size="37" />
          <FlexLayout align="center" justify="center" class="w-full">
            <el-progress
              style="width: 100%; padding: 0"
              :percentage="progressPercentage"
              :stroke-width="20"
              :text-inside="true"
              color="var(--primary-100)"
            />
          </FlexLayout>
          <p class="text-sub text-gray-80">{{ $t('member.memberCardSetting.ai.generating') }}</p>
        </FlexLayout>
      </template>
    </OverlayArea>

    <WarningDialog
      v-if="modal.warning"
      :append-to-body="true"
      @close="onWarnClose"
      @confirm="onConfirm(true)"
    >
      <template slot="body">
        <div style="padding-top: 16px">
          {{ $t('member.memberCardSetting.ai.warningContent') }}
        </div>
      </template>
    </WarningDialog>
  </BaseDialog>
</template>

<script>
import { computed, defineComponent, reactive, ref } from 'vue'
import BaseDialog from '@/components/Dialog/BaseDialog.vue'
import { useBaseForm } from '@/use/useForm'
import SVGIcon from '@/components/SVGIcon.vue'
import ElInputWrapper from '@/components/ElInputWrapper.vue'
import FormItemTooltip from '@/components/FormItem/FormItemTooltip.vue'
import WarningDialog from '@/components/Dialog/WarningDialog.vue'
import UploadFileWithFileNameButton from '@/components/UploadFileWithFileNameButton.vue'
import { CreateImageGenerationTask, FindOneImageGenerationTask } from '@/api/imageGeneration'
import { useShop } from '@/use/shop'
import { useFakeProgress } from '@/use/useFakeProgress'
import { get, map, toString } from 'lodash'
import notifyMessage from '@/config/notifyMessage'
import { useS3 } from '@/use/useS3'
import Spin from '@/components/Spin.vue'
import { t } from '@/plugins/i18n/i18n'

export default defineComponent({
  name: 'AIGenerateMemberCardImageModal',
  components: {
    Spin,
    BaseDialog,
    WarningDialog,
    SVGIcon,
    ElInputWrapper,
    FormItemTooltip,
    UploadFileWithFileNameButton,
  },
  props: {
    aiGenerateConfig: {
      type: Object,
      default: null,
    },
  },
  setup (props, { emit }) {
    const { shopId } = useShop()
    const { uploadToS3 } = useS3()
    const { formRef, formData, initFormData, checkForm, loading } = useBaseForm()
    const { progressPercentage, startProgress, completeProgress, resetProgress } = useFakeProgress()
    const cancelled = ref(false)

    const modal = reactive({
      warning: false,
    })

    // 初始化表單數據
    const levelPrompts = get(props.aiGenerateConfig, 'levelPrompts', [])

    // 檢查是否有自訂內容（任何等級文字）
    const hasLevelText = levelPrompts.some(prompt => {
      const text = get(prompt, 'text')
      return text !== null && text !== undefined && String(text).trim() !== ''
    })

    // 初始化 levelSettings，從 levelPrompts 映射文字
    const initialLevelSettings = [
      { customText: get(levelPrompts[0], 'text', '') || '' },
      { customText: get(levelPrompts[1], 'text', '') || '' },
      { customText: get(levelPrompts[2], 'text', '') || '' },
      { customText: get(levelPrompts[3], 'text', '') || '' },
    ]

    initFormData({
      imageStyle: get(props.aiGenerateConfig, 'style', undefined),
      textPrompt: get(props.aiGenerateConfig, 'textPrompt', undefined),
      enableCustomContent: hasLevelText,
      customLogo: null,
      levelSettings: initialLevelSettings,
    })

    // 圖片風格選項
    const imageStyleOptions = computed(() => [
      { value: '復古/花紋壁紙', label: t('member.memberCardSetting.ai.styles.vintageWallpaper') },
      { value: '復古/洛可可', label: t('member.memberCardSetting.ai.styles.vintageRococo') },
      { value: '溫馨/水彩', label: t('member.memberCardSetting.ai.styles.warmWatercolor') },
      { value: '奢華/金屬', label: t('member.memberCardSetting.ai.styles.luxuryMetal') },
      { value: '奢華/中式', label: t('member.memberCardSetting.ai.styles.luxuryChinese') },
      { value: '奢華/流金', label: t('member.memberCardSetting.ai.styles.luxuryGold') },
      { value: '商務/科技', label: t('member.memberCardSetting.ai.styles.businessTech') },
      { value: '簡約/中性', label: t('member.memberCardSetting.ai.styles.minimalNeutral') },
      { value: '簡約/材質', label: t('member.memberCardSetting.ai.styles.minimalMaterial') },
      { value: '簡約/漸層', label: t('member.memberCardSetting.ai.styles.minimalGradient') },
      { value: '時尚/鍍鉻', label: t('member.memberCardSetting.ai.styles.fashionChrome') },
    ])

    // 自定義驗證器：確保 imageStyle 和 textPrompt 至少填寫一個
    const validateStyleOrTextPrompt = (rule, value, callback) => {
      const hasStyle = formData.imageStyle && formData.imageStyle.trim() !== ''
      const hasTextPrompt = formData.textPrompt && formData.textPrompt.trim() !== ''
      if (!hasStyle && !hasTextPrompt) {
        callback(new Error(t('member.memberCardSetting.ai.styleError')))
        return
      }
      callback()
    }

    // 表單驗證規則
    const formRules = computed(() => ({
      style: [{ validator: validateStyleOrTextPrompt, trigger: 'blur' }],
    }))

    // 顯示文字
    const displayText = computed(() => ({
      title: t('member.memberCardSetting.ai.title'),
      description: t('member.memberCardSetting.ai.description'),
      formItemLabel: {
        imageStyle: t('member.memberCardSetting.ai.formLabel.imageStyle'),
        otherStyle: t('member.memberCardSetting.ai.formLabel.otherStyle'),
        enableCustomContent: t('member.memberCardSetting.ai.formLabel.enableCustomContent'),
        logo: t('member.memberCardSetting.ai.formLabel.logo'),
      },
      placeholder: {
        imageStyle: t('member.memberCardSetting.ai.placeholder.imageStyle'),
        otherStyle: t('member.memberCardSetting.ai.placeholder.otherStyle'),
        customText: t('member.memberCardSetting.ai.placeholder.customText'),
      },
      switch: {
        yes: t('member.memberCardSetting.ai.switch.yes'),
        no: t('member.memberCardSetting.ai.switch.no'),
      },
      tooltip: {
        customContent: t('member.memberCardSetting.ai.tooltip.customContent'),
      },
      sectionTitle: {
        levelSettings: t('member.memberCardSetting.ai.sectionTitle.levelSettings'),
      },
      btn: {
        generate: t('member.memberCardSetting.ai.btn.generate'),
        cancel: t('member.memberCardSetting.ai.btn.cancel'),
      },
      warning: t('member.memberCardSetting.ai.warning'),
      styleOrOtherStyle: t('member.memberCardSetting.ai.styleOrOtherStyle'),
    }))

    const submitPayload = computed(() => {
      const payload = {
        shopId: shopId.value,
        type: 'memberCard',
        input: {
          memberCardSetting: {
            style: formData.imageStyle || null,
            textPrompt: formData.textPrompt || null,
            // 只傳已上傳的圖片資訊給後端
            logoImagePath: formData.customLogo?.uploaded?.value || null,
            levelPrompts: map(formData.levelSettings, (level, idx) => {
              return {
                level: toString(idx + 1),
                text: level.customText || null,
              }
            }),
          },
        },
      }

      return payload
    })

    // 確認生成
    const onConfirm = async (force = false) => {
      modal.warning = false
      if (loading.value) return
      loading.value = true
      const formValid = await checkForm(formRef.value)
      if (!formValid) {
        loading.value = false
        return
      }

      if (!force) {
        modal.warning = true
        loading.value = false
        return
      }

      loading.value = true

      // 開始模擬進度
      startProgress()

      const [res, errMsg] = await CreateImageGenerationTask(submitPayload.value)
      if (errMsg) {
        loading.value = false
        resetProgress()
        window.$message.error(errMsg)
        return
      }

      // 透過 FindImageGenerationTask 輪詢直到任務完成
      // 根據 FindImageGenerationTask 結果的 status 判斷任務狀態
      // status 可能值：pending, processing, completed, failed
      if (res && res.id) {
        let taskStatus = 'pending'
        let taskResult = null
        let errorCode = null
        let errorMessage = null
        while (taskStatus === 'pending' || taskStatus === 'processing') {
          // stop polling if dialog was closed
          if (cancelled.value) {
            loading.value = false
            resetProgress()
            return
          }
          // eslint-disable-next-line no-await-in-loop
          const [taskRes, errMsg] = await FindOneImageGenerationTask({ id: res.id, shopId: shopId.value })
          if (errMsg) {
            loading.value = false
            resetProgress()
            window.$message.error(errMsg)
            return
          }
          if (taskRes) {
            taskStatus = taskRes.status
            taskResult = taskRes.result
            errorCode = get(taskRes, 'errorCode')
            errorMessage = get(taskRes, 'errorMessage')
          }
          // 每 2 秒輪詢一次
          // eslint-disable-next-line no-await-in-loop
          await new Promise(resolve => setTimeout(resolve, 2000))
        }

        if (taskStatus === 'completed') {
          completeProgress()
          // 給用戶看到 100% 的時間
          await new Promise(resolve => setTimeout(resolve, 500))
          window.$message.success(notifyMessage.createSuccess)
          emit('generated', taskResult)
          handleClose()
        } else if (taskStatus === 'failed') {
          resetProgress()
          window.$message.error(t('member.memberCardSetting.ai.errorCode', { code: errorCode, message: errorMessage }))
        }
      }

      loading.value = false
    }

    // 關閉彈窗
    const handleClose = () => {
      // set cancelled so any running polling will stop
      cancelled.value = true
      resetProgress()

      // 將當前的配置回傳給父組件
      // 保持與輸入格式一致的結構
      const currentConfig = {
        memberCardSetting: {
          style: formData.imageStyle || null,
          textPrompt: formData.textPrompt || null,
          logoImagePath: formData.customLogo?.uploaded?.value || null,
          levelPrompts: map(formData.levelSettings, (level, idx) => ({
            level: toString(idx + 1),
            text: level.customText || null,
          })),
        },
      }

      emit('close', currentConfig)
    }

    const onWarnClose = () => {
      loading.value = false
      modal.warning = false
      resetProgress()
    }

    const onUploadLogoChanged = (payload) => {
      formData.customLogo = payload
    }

    return {
      modal,
      formData,
      formRef,
      formRules,
      imageStyleOptions,
      displayText,
      loading,
      progressPercentage,
      onConfirm,
      handleClose,
      onWarnClose,
      onUploadLogoChanged,
      uploadToS3,
    }
  },
})
</script>

<style lang="postcss" scoped>
.level-header {
  @apply mb-3;
}

.level-title {
  @apply text-normal font-medium text-gray-100;
}

::v-deep .el-progress-bar {
  @apply p-0;
}
</style>
